import { EyeOutlined, UsergroupAddOutlined } from '@ant-design/icons'
import { Button, Result, Tabs, Tooltip } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom'

import { AppHeader, ColumnLayout, GroupAvatar } from '@/components'
import {
  Group,
  GroupPrivateSetting,
  WhoCanPost,
  useGetGroupQuery,
  useGroupUpdatedSubscription,
} from '@/generated/graphql'
import { FileContainer } from '@/modules/file/file.container'
import { GroupMenu } from '@/modules/group/group-menu'
import { GroupSidebar } from '@/modules/group/group-sidebar'
import { useIsGroupAdmin } from '@/modules/group/group.hooks'
import { PostsContainer } from '@/modules/post/posts.container'
import { routes } from '@/routes'

import { ApplyButton } from './group-apply/group-apply-button.component'
import s from './groups.module.less'

interface State {
  tab: string | undefined
}

interface GroupContainerProps {
  groupId?: number
}

export const GroupContainer: React.FC<GroupContainerProps> = ({ groupId: gid }) => {
  const history = useHistory()
  const match = useRouteMatch<{ groupId: string }>('/groups/:groupId')
  const { t } = useTranslation()
  const location = useLocation()

  const groupId = gid || parseInt(match?.params.groupId || '')

  const { data, loading, error } = useGetGroupQuery({
    skip: !(groupId >= 1),
    variables: {
      groupId,
      withAdmins: true,
      pagination: {
        first: 1000,
      },
    },
  })

  const [state, setState] = React.useState<State>({
    tab: 'posts',
  })

  useGroupUpdatedSubscription({
    variables: {
      groupId,
    },
  })

  React.useEffect(() => {
    if (!location.pathname.startsWith(routes.groups.home())) {
      return
    }
    if (groupId >= 1) {
      history.replace(routes.groups.page(groupId))
      // NaN 或其他非法数字
    } else {
      history.push(routes.groups.home())
    }
  }, [])

  // 点击左侧栏动态消息 Menu 刷新
  React.useEffect(() => {
    window.scrollTo({ top: 0 })
    setState({ tab: 'posts' })
  }, [location.key])

  const { isGroupAdmin } = useIsGroupAdmin(data?.group as Group)

  if (error) {
    return (
      <div style={{ height: '90vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Result
          status='404'
          title={t('page.group.notFound')}
          extra={
            <Button type='primary' onClick={() => history.goBack()}>
              Back
            </Button>
          }
        />
      </div>
    )
  }

  if (loading || !data) {
    return null
  }

  const group = data.group as Group

  const isGeneralGroup = group.groupId === 6 || group.groupId === 23

  let middle: JSX.Element | undefined
  let right: JSX.Element | undefined
  let tabs: JSX.Element | undefined

  if (group.privateSetting === GroupPrivateSetting.Closed && !group.isFollowing) {
    middle = (
      <div style={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Result
          icon={<UsergroupAddOutlined />}
          title={t('group.applyFollowPrivateGroup')}
          subTitle={t('group.applyTip')}
          extra={<ApplyButton groupId={group.groupId} />}
        />
      </div>
    )
  } else {
    middle = (() => {
      switch (state.tab) {
        case 'posts':
          return <PostsContainer group={group} />
        case 'files':
          return <FileContainer group={group} />
      }
    })()
    right = <GroupSidebar group={group} isGroupAdmin={isGroupAdmin} />
    tabs = (
      <Tabs animated={false} activeKey={state.tab} onChange={(tab) => setState({ tab })} destroyInactiveTabPane>
        <Tabs.TabPane tab={t('group.postsMenu')} key='posts' />
        <Tabs.TabPane tab={t('group.filesMenu')} key='files' />
      </Tabs>
    )
  }

  const title = (
    <span className={s.title}>
      <span>{group.name}</span>
      {group.category && !group.isOfficial && !isGeneralGroup && (
        <span className={s.category}>
          <Link to={routes.discover.groups(group.category.groupCategoryId)}>
            {t(`groups.category.${group.category.groupCategoryId}`)}
          </Link>
        </span>
      )}
      <span className={s.whoCanPost}>
        {t(`groups.whoCanPost.${String(WhoCanPost[group.whoCanPost]).toLowerCase()}`)}
      </span>
      {group.privateSetting === GroupPrivateSetting.Closed && (
        <span className={s.whoCanPost}>{t(`group.privateSetting.closedGroup`)}</span>
      )}
      {group.pmoProjectId && (
        <a target='_blank' href={group.pmoLink!} rel='noopener noreferrer' className={s.whoCanPost}>
          {t('group.pmo')}
        </a>
      )}
      {!group.canPostAnonymously && (
        <Tooltip title={t('group.notSupportAnonymous')}>
          <span className={s.postAnonymously}>
            <EyeOutlined />
          </span>
        </Tooltip>
      )}
    </span>
  )

  const header = (
    <AppHeader
      title={title}
      titleString={group.name}
      avatar={
        <GroupAvatar
          style={{ fontSize: 24 }}
          isOfficial={group.isOfficial}
          groupCatId={group.category?.groupCategoryId}
        />
      }
      actions={<GroupMenu group={group} />}
    >
      {tabs}
    </AppHeader>
  )

  return <ColumnLayout header={header} middle={middle} right={right} />
}
